import axios from "axios";
import moment from "moment";

import { getMandatoryUserData, getUserCredentials, normalizeCustomerType } from "../utils/common";
import { API_URL } from "../utils/Constant";

/** Default credits lookback: last 1 month as YYYY-MM-DD,YYYY-MM-DD (inclusive). */
export const getDefaultCreditsDateRange = () => {
  const end = moment();
  const start = moment().subtract(1, "month");
  return `${start.format("YYYY-MM-DD")},${end.format("YYYY-MM-DD")}`;
};

/**
 * Remaining credits (enhanced).
 * GET /api/usage/credits/
 *
 * Query:
 * - user_payload (required; interceptor)
 * - location_name (required; branch name / old company_name)
 * - date_range (required; YYYY-MM-DD,YYYY-MM-DD inclusive)
 * - all_location (optional; only true / false; omit = false)
 *
 * company_domain is ignored by the API.
 * Remaining = current wallet. *_consumed_credits = consume minus refund in date_range only.
 * all_location=true sums grouped locations. One row per active category (product + env).
 *
 * @param {{
 *   locationName?: string,
 *   dateRange?: string,
 *   allLocation?: boolean,
 *   signal?: AbortSignal,
 * } | AbortSignal} [optionsOrSignal]
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   wallet_customer_id?: string,
 *   source_customer_id?: string,
 *   customer_type?: string,
 *   total_credits?: string | number,
 *   location_name?: string,
 *   all_location?: boolean,
 *   date_range?: { start?: string, end?: string },
 *   group_id?: string,
 *   independent_flag?: boolean,
 *   locations_list?: Array<{ location_name: string, customer_id: string }>,
 *   credits?: Array,
 *   grace_period_status?: string | null,
 *   grace_ends_on?: string | null,
 *   grace_days_left?: number | null,
 *   grace_amount_due?: string | number | null,
 *   grace_days_overdue?: number | null,
 *   invoice_id?: string | number | null,
 *   invoice_amount_due?: string | number | null,
 *   invoice_pdf_path?: string | null,
 * }>}
 */
export const fetchUsageCredits = async (optionsOrSignal = null) => {
  try {
    const options = optionsOrSignal instanceof AbortSignal
      ? { signal: optionsOrSignal }
      : (optionsOrSignal || {});
    const {
      locationName,
      dateRange,
      allLocation,
      signal = null,
    } = options;

    const user = getUserCredentials() || {};
    const params = {
      location_name: locationName || user.company_name,
      date_range: dateRange || getDefaultCreditsDateRange(),
    };

    if (allLocation === true) {
      params.all_location = true;
    } else if (allLocation === false) {
      params.all_location = false;
    }

    const resp = await axios.get(`${API_URL}/usage/credits/`, {
      params,
      signal,
    });

    const data = resp.data;
    const result = data.result && !Array.isArray(data.result) ? data.result : {};
    const success = data.message === "Success";

    return {
      success,
      message: data.message || (success ? "Success" : "Something went wrong."),
      wallet_customer_id: result.wallet_customer_id,
      source_customer_id: result.source_customer_id,
      customer_type: normalizeCustomerType(result.customer_type),
      total_credits: result.total_credits,
      location_name: result.location_name,
      all_location: result.all_location,
      date_range: result.date_range,
      group_id: result.group_id,
      independent_flag: result.independent_flag,
      locations_list: Array.isArray(result.locations_list) ? result.locations_list : [],
      credits: Array.isArray(result.credits) ? result.credits : [],
      grace_period_status: result.grace_period_status ?? null,
      grace_ends_on: result.grace_ends_on ?? null,
      grace_days_left: result.grace_days_left ?? null,
      grace_amount_due: result.grace_amount_due ?? null,
      grace_days_overdue: result.grace_days_overdue ?? null,
      invoice_id: result.invoice_id ?? null,
      invoice_amount_due: result.invoice_amount_due ?? null,
      invoice_pdf_path: result.invoice_pdf_path ?? null,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Usage categories for plan builder pricing.
 * GET /api/usage-categories/
 *
 * Response shape:
 * {
 *   count,
 *   result: [{
 *     id, code, name, cost, credits, status,
 *     created_at, updated_at, created_by_id, updated_by_id,
 *   }],
 * }
 *
 * @param {{
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   count?: number,
 *   results?: Array,
 * }>}
 */
export const getUsageCategories = async ({
  signal = null,
} = {}) => {
  try {
    const resp = await axios.get(`${API_URL}/usage-categories/`, {
      signal,
    });

    const data = resp.data;
    const results = data.result;
    const success = Array.isArray(results);

    return {
      success,
      message: data.message || (success ? "Success" : "Something went wrong."),
      count: data.count,
      results,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Tests grouped by usage category for the View tests modal.
 * GET /api/usage-categories/tests/?usage_category_id=1,7
 *
 * Response shape:
 * {
 *   count,
 *   result: [{
 *     id, code, name, cost, credits, side, subcategory,
 *     maximum_availability, status, usage_category_id,
 *     usage_category_code, usage_category_name,
 *     tests: [{
 *       id, internal_name, display_name, status,
 *       usage_category_id, is_env_testable,
 *     }],
 *   }],
 * }
 *
 * @param {{
 *   usageCategoryIds?: string|number|Array<string|number>,
 *   status?: number|string|null,
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   count?: number,
 *   categories?: Array,
 * }>}
 */
export const fetchUsageCategoryTests = async ({
  usageCategoryIds,
  status = 1,
  signal = null,
} = {}) => {
  try {
    const params = {};

    if (usageCategoryIds != null && usageCategoryIds !== "") {
      params.usage_category_id = Array.isArray(usageCategoryIds)
        ? usageCategoryIds.join(",")
        : usageCategoryIds;
    }

    if (status != null && status !== "") {
      params.status = status;
    }

    const resp = await axios.get(`${API_URL}/usage-categories/tests/`, {
      params,
      signal,
    });

    const data = resp.data;
    const categories = data.result;
    const success = Array.isArray(categories);

    return {
      success,
      message: data.message || (success ? "Success" : "Something went wrong."),
      count: data.count,
      categories,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Outstanding subscription amount for review modal.
 * GET /api/usage/outstanding/
 * Interceptor injects user_payload + company_name.
 * Pass companyName to query a specific location; omit it for all locations.
 *
 * @param {{
 *   signal?: AbortSignal,
 *   companyName?: string,
 * }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   result?: {
 *     customer_id?: string,
 *     plan_type?: string,
 *     currency?: string,
 *     billing_cycle?: string,
 *     term?: number,
 *     billing_amount?: string,
 *     subscription_mode?: string,
 *     addon_amount?: string,
 *     unpaid_invoice_amount?: string,
 *     outstanding?: string,
 *     current_period_start?: string,
 *     current_period_end?: string,
 *     next_billing_date?: string,
 *     invoices?: Array,
 *   },
 *   grace_period_status?: string,
 *   grace_ends_on?: string | null,
 *   grace_days_left?: number | null,
 *   grace_amount_due?: string | number | null,
 *   grace_days_overdue?: number | null,
 *   [key: string]: *,
 * }>}
 */
export const fetchUsageOutstanding = async ({
  signal = null,
  companyName,
} = {}) => {
  try {
    const params = {};
    if (companyName != null && companyName !== "") {
      const { user_payload, company_domain } = getMandatoryUserData();
      params.user_payload = user_payload;
      params.company_domain = company_domain;
      params.company_name = companyName;
      params.disableInterceptor = true;
    }

    const resp = await axios.get(`${API_URL}/usage/outstanding/`, {
      params,
      signal,
    });

    const data = resp.data;
    const success = data.message === "Success";

    return {
      success,
      message: data.message,
      ...data,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Usage ledger (test activity history).
 * GET /api/usage/ledger/
 * Interceptor injects user_payload + company_name.
 *
 * @param {{
 *   search?: string,
 *   usageCategoryCode?: string,
 *   dateFrom?: string,
 *   dateTo?: string,
 *   page?: number,
 *   pageSize?: number,
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   count?: number,
 *   page?: number,
 *   page_size?: number,
 *   activities?: Array,
 * }>}
 */
export const fetchUsageLedger = async ({
  search,
  usageCategoryCode,
  dateFrom,
  dateTo,
  page = 1,
  pageSize = 20,
  signal = null,
} = {}) => {
  try {
    const params = {
      page,
      page_size: Math.min(Math.max(Number(pageSize), 1), 100),
    };

    if (search != null && search !== "") {
      params.search = search;
    }
    if (usageCategoryCode != null && usageCategoryCode !== "") {
      params.usage_category_code = usageCategoryCode;
    }
    if (dateFrom != null && dateFrom !== "") {
      params.date_from = dateFrom;
    }
    if (dateTo != null && dateTo !== "") {
      params.date_to = dateTo;
    }

    const resp = await axios.get(`${API_URL}/usage/ledger/`, {
      params,
      signal,
    });

    const data = resp.data;
    const result = data.result;
    const success = data.message === "Success";

    return {
      success,
      message: data.message,
      count: result.count,
      page: result.page,
      page_size: result.page_size,
      activities: result.activities,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message,
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Recharge (top-up) credits.
 * POST /api/usage/recharge/
 * Sends credits + idempotency_key. Also includes user_payload, company_name,
 * and company_domain (interceptor injects these as well if missing).
 * Do not send customer_id.
 *
 * @param {{
 *   credits: Array<{ usage_category_code: string, quantity: number }>,
 *   idempotencyKey?: string,
 *   notes?: string,
 *   actionBy?: string,
 *   signal?: AbortSignal,
 * }} options
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   wallet_customer_id?: string,
 *   source_customer_id?: string,
 *   granted?: Array,
 * }>}
 */
export const rechargeUsageCredits = async ({
  credits,
  idempotencyKey,
  notes,
  actionBy,
  signal = null,
} = {}) => {
  try {
    const {
      user_payload,
      company_name,
      company_domain,
    } = getMandatoryUserData();

    const payload = {
      credits,
      user_payload,
      company_name,
      company_domain,
    };

    if (idempotencyKey != null && idempotencyKey !== "") {
      payload.idempotency_key = idempotencyKey;
    }
    if (notes != null && notes !== "") {
      payload.notes = notes;
    }
    if (actionBy != null && actionBy !== "") {
      payload.action_by = actionBy;
    }

    const resp = await axios.post(`${API_URL}/usage/recharge/`, payload, {
      signal,
    });

    const data = resp.data;
    const result = data.result;
    const success = data.message === "Success";

    return {
      success,
      message: data.message,
      granted: result.granted,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Add credits to subscription plan.
 * POST /api/usage/topup/
 * Sends amount, credits, user_payload, and company_name.
 *
 * @param {{
 *   credits: Array<{ usage_category_code: string, quantity: number }>,
 *   amount: number,
 *   idempotencyKey?: string,
 *   notes?: string,
 *   actionBy?: string,
 *   signal?: AbortSignal,
 * }} options
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   [key: string]: *,
 * }>}
 */
export const topupUsageCredits = async ({
  credits,
  amount,
  idempotencyKey,
  notes,
  actionBy,
  signal = null,
} = {}) => {
  try {
    const {
      user_payload,
      company_name,
    } = getMandatoryUserData();

    const payload = {
      user_payload,
      company_name,
      amount: Number(Number(amount).toFixed(2)),
      credits,
    };

    if (idempotencyKey != null && idempotencyKey !== "") {
      payload.idempotency_key = idempotencyKey;
    }
    if (notes != null && notes !== "") {
      payload.notes = notes;
    }
    if (actionBy != null && actionBy !== "") {
      payload.action_by = actionBy;
    }

    const resp = await axios.post(`${API_URL}/usage/topup/`, payload, {
      signal,
    });

    const data = resp.data;
    const success = data.message === "Success";

    return {
      success,
      message: data.message,
      ...data,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Multi-location credit consumption graph.
 * GET /api/usage/credit-consumption-graph/
 *
 * Supports group_by=category and group_by=location.
 * location is optional; omit or send empty to include every location.
 *
 * @param {{
 *   companyName?: string,
 *   groupBy?: "category" | "location",
 *   location?: string,
 *   startDate: string,
 *   endDate: string,
 *   signal?: AbortSignal,
 * }} options
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   group_by?: string,
 *   start_date?: string,
 *   end_date?: string,
 *   data?: Array,
 *   series?: Array,
 *   total_number_tests?: number | string | null,
 *   test_growth?: number | string | null,
 *   highest_consumption_location?: { location_name?: string, percent?: number | string | null } | null,
 *   remaining_days?: number | string | null,
 * }>}
 */
export const fetchCreditConsumptionGraph = async ({
  companyName,
  groupBy = "category",
  location = "",
  startDate,
  endDate,
  signal = null,
} = {}) => {
  try {
    const user = getUserCredentials() || {};
    const company_name = companyName || user.company_name;

    if (!company_name || !startDate || !endDate) {
      return {
        success: false,
        message: "company_name, start_date, and end_date are required.",
      };
    }

    const resp = await axios.get(`${API_URL}/usage/credit-consumption-graph/`, {
      params: {
        company_name,
        group_by: groupBy,
        location,
        start_date: startDate,
        end_date: endDate,
      },
      signal,
    });

    const data = resp.data;
    const result = data.result || data;
    const successMessage = String(data.message || "").toLowerCase() === "success";
    const success = data.success === true || successMessage;
    const readResultField = (key) => (
      result?.[key] !== undefined ? result[key] : data?.[key]
    );

    if (!success) {
      return {
        success: false,
        message: data.error || (successMessage ? undefined : data.message) || "Unable to load credit consumption graph.",
        group_by: result.group_by ?? data.group_by,
        start_date: result.start_date ?? data.start_date,
        end_date: result.end_date ?? data.end_date,
      };
    }

    return {
      success: true,
      message: data.message,
      group_by: result.group_by ?? data.group_by,
      start_date: result.start_date ?? data.start_date,
      end_date: result.end_date ?? data.end_date,
      data: Array.isArray(result.data) ? result.data : [],
      series: Array.isArray(result.series) ? result.series : [],
      total_number_tests: readResultField("total_number_tests") ?? null,
      test_growth: readResultField("test_growth") ?? null,
      highest_consumption_location: readResultField("highest_consumption_location") ?? null,
      remaining_days: readResultField("remaining_days") ?? null,
    };
  } catch (e) {
    const errorMessage = e.response?.data?.error || e.response?.data?.message;
    return {
      success: false,
      message: String(errorMessage || "").toLowerCase() === "success"
        ? "Unable to load credit consumption graph."
        : errorMessage || "Unable to load credit consumption graph.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Creates a Gynger checkout session (usage recharge or invoice renewal).
 * POST /api/gynger/checkout/
 *
 * Amounts are sent in cents (amount * 100).
 *
 * @param {{
 *   amount: number,
 *   credits?: Array<{ usage_category_code: string, quantity: number }>,
 *   purpose?: string,
 *   productDescription?: string,
 *   buyerEmail?: string,
 *   invoiceId?: string,
 *   signal?: AbortSignal,
 * }} options
 * @returns {Promise<{
 *   success: boolean,
 *   id?: string,
 *   session_id?: string,
 *   session_url?: string,
 *   status?: string,
 *   message?: string,
 *   canceled?: boolean,
 *   [key: string]: any,
 * }>}
 */
export const openPaymentSession = async ({
  amount,
  credits = [],
  purpose = "recharge",
  productDescription = "Usage Metrics Top-Up",
  buyerEmail,
  invoiceId,
  signal = null,
} = {}) => {
  try {
    const user = getUserCredentials() || {};
    const payload = {
      purpose,
      amount: Math.round(Number(amount) * 100),
      productDescription,
      buyerEmail: buyerEmail || user.user_email || "",
    };

    // Invoice Pay Now always settles a specific invoice, including top-up
    // (purpose "recharge"). Credit purchases have no invoice id and send credits.
    if (invoiceId) {
      payload.invoice_id = invoiceId;
    } else if (purpose !== "invoice" && purpose !== "negative_credit") {
      payload.credits = credits;
    }

    const resp = await axios.post(`${API_URL}/gynger/checkout/`, payload, {
      signal,
    });

    const data = resp.data || {};

    if (!data.id || !data.session_url) {
      return {
        success: false,
        message: "Unable to open payment session.",
      };
    }

    return {
      success: true,
      ...data,
      session_id: data.id,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || e.response?.data?.error || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Polls Gynger checkout session status.
 * GET /api/gynger/checkout/?session_id=
 *
 * @param {string} sessionId
 * @param {AbortSignal} [signal]
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   result?: {
 *     session_id?: string,
 *     purpose?: string,
 *     status?: string,
 *     gynger_status?: string,
 *     webhook_called?: boolean,
 *     webhook_call_count?: number,
 *     webhook_called_at?: string,
 *   },
 *   statusCode?: number,
 *   canceled?: boolean,
 * }>}
 */
export const fetchGyngerCheckoutStatus = async (sessionId, signal = null) => {
  try {
    const resp = await axios.get(`${API_URL}/gynger/checkout/`, {
      params: {
        session_id: sessionId,
      },
      signal,
    });

    const data = resp.data;
    const success = data.message === "Success";

    return {
      success,
      message: data.message || (success ? "Success" : "Something went wrong."),
      result: data.result,
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || e.response?.data?.error || "Something went wrong.",
      statusCode: e.response?.status,
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Classify requested tests for credit usage.
 * POST /api/usage/classify-tests/
 * Sends user_payload, company_name, and samples (sample_id, side, tests).
 *
 * @param {{
 *   samples: Array<{
 *     sample_id: string,
 *     side: "product" | "environment" | string,
 *     tests: string[],
 *   }>,
 *   signal?: AbortSignal,
 * }} options
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   canceled?: boolean,
 *   [key: string]: *,
 * }>}
 */
export const classifyUsageTests = async ({
  samples = [],
  signal = null,
} = {}) => {
  try {
    const {
      user_payload,
      company_name,
    } = getMandatoryUserData();

    const payload = {
      user_payload,
      company_name,
      samples,
    };

    const resp = await axios.post(`${API_URL}/usage/classify-tests/`, payload, {
      signal,
    });

    const data = resp.data;
    const success = data.message === "Success" || data.success === true;

    return {
      ...data,
      success,
      message: data.message || (success ? "Success" : "Something went wrong."),
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || e.response?.data?.error || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};
